<template>
  <div class="records-filter-bar">
    <!--
      Leading, and named for what it opens rather than for adding one more: it is the way into
      filtering whether or not anything is filtered yet, so it reads as the set's handle rather than
      as the last item in it. Outside the rail, so it is never the thing that gets clipped.
    -->
    <hr-assist-chip
      :label="i18n.t('common.buttonFiltersLabel')"
      :legacy-props="LEGACY_CHIP_PROPS"
      icon="filter_alt"
      data-e2e="chip-add-filter"
      class="records-filter-bar__chip"
      @click="openDialog(null)"
    />

    <!--
      Every filter is rendered whatever fits. The rail clips, widths are measured while they are
      all on screen, and what fits is arithmetic against a width that does not change when a chip
      is hidden — so the answer cannot move the thing it was measured from.
    -->
    <div
      ref="railRef"
      class="records-filter-bar__rail"
    >
      <div
        v-for="filterKey in filterKeys"
        v-show="visibleKeys.includes(filterKey)"
        :key="filterKey"
        :data-filter-key="filterKey"
        class="records-filter-bar__group"
      >
        <hr-input-chip
          v-for="(statement, idx) in statementsOf(filterKey)"
          :key="`${filterKey}-${idx}`"
          :label="statement.label"
          :remove-aria-label="i18n.t('common.filter.buttonRemoveFilterLabel', {
            filter: statement.label,
          })"
          clickable
          class="records-filter-bar__chip"
          @click="openDialog(filterKey)"
          @remove="onRemove(filterKey, idx)"
        />
      </div>

      <!--
        Beside the chips it counts, inside the rail: a hidden chip is display:none and takes no
        space, so the rail's contents end where the visible ones do. Outside it, the chip would sit
        at the far end of a row the rail has already filled.
      -->
      <div
        v-if="hiddenKeys.length"
        class="records-filter-bar__group"
      >
        <hr-status-action-chip
          v-model="isOverflowOpen"
          chevron
          :label="`+${hiddenKeys.length}`"
          :aria-label="i18n.tc('common.filter.buttonMoreFiltersLabel', hiddenKeys.length, {
            count: hiddenKeys.length,
          })"
          data-e2e="chip-more-filters"
          class="records-filter-bar__chip"
        >
          <!-- The chip owns the open state, so the menu follows it rather than opening itself. -->
          <hr-menu
            v-model="isOverflowOpen"
            no-parent-event
          >
            <div class="records-filter-bar__overflow-panel">
              <template
                v-for="filterKey in hiddenKeys"
                :key="filterKey"
              >
                <hr-input-chip
                  v-for="(statement, idx) in statementsOf(filterKey)"
                  :key="`${filterKey}-${idx}`"
                  :label="statement.label"
                  :remove-aria-label="i18n.t('common.filter.buttonRemoveFilterLabel', {
                    filter: statement.label,
                  })"
                  clickable
                  class="records-filter-bar__chip"
                  @click="openDialog(filterKey)"
                  @remove="onRemove(filterKey, idx)"
                />
              </template>
            </div>
          </hr-menu>
        </hr-status-action-chip>
      </div>
    </div>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';

import { removeFilterStatement } from 'components/UI/filterDialog/filterMap';
import createFilterDialogOpener from 'components/UI/filterDialog/openFilterDialog';
import { visibleFilterKeys } from 'components/UI/recordsFilterOverflow';
import { HrAssistChip, HrInputChip, HrMenu, HrStatusActionChip } from 'src/design-system';

/**
 * The filter chips above a records table: the applied filters, the way into the dialog that edits
 * them, and what happens when there are more of them than the row can hold.
 *
 * **Separate from FilterChipsBar on purpose.** That component is on nineteen other screens, in
 * dialogs and panels and report editors, where a row that clips and counts would be wrong and
 * where nobody asked for one. The overflow behaviour here belongs to a records page, so it lives
 * with the records page rather than being pushed into everything that shows a filter chip.
 *
 * **How the set overflows is decided by the input and the room, not by one of them.** A touch
 * pointer scrolls the rail, because a thumb already knows how to push a row along. A fine pointer
 * collapses what will not fit into a "+N" chip — but only above a phone width, since a row narrow
 * enough to collapse hides nearly every chip behind a count.
 */
defineOptions({ name: 'RecordsFilterBar' });

/*
 * Filter chips have always been primary/white here, so pin that for the legacy theme; it drops out
 * automatically under a design-system theme.
 * @todo Remove when the legacy theme is removed.
 */
const LEGACY_CHIP_PROPS = { color: 'primary' };

// The gap the chips are spaced by, which the arithmetic accounts for as well as their widths.
const CHIP_GAP = 8;

// Below this the rail scrolls whatever the pointer, matching the stylesheet below.
const NARROW_QUERY = '(max-width: 599px)';

const props = defineProps({
  /** The filters currently applied, keyed by criterion. */
  filters: {
    type: Object,
    required: true,
  },
  /** Every criterion the reader may filter on. */
  filterCriteria: {
    type: Array,
    required: true,
  },
  /** Group labels the caller declared. No grouping is invented from them. */
  filterGroups: {
    type: Array,
    default: () => ([]),
  },
});

const emit = defineEmits({
  /** Fired with the new filter map whenever a filter is added, changed or removed. */
  'update:filters': null,
});

const railRef = ref(null);
const isFinePointer = ref(false);
const isNarrow = ref(false);
const isOverflowOpen = ref(false);
const groupWidths = ref(new Map());
const railWidth = ref(0);

const mediaQuery = (query) => (
  typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null
);

const pointerQuery = mediaQuery('(pointer: fine)');
const narrowQuery = mediaQuery(NARROW_QUERY);

/*
 * Collapsing is for a pointer with room to use it. On touch the rail scrolls, and at a phone width
 * it scrolls whatever the pointer — a row that narrow would hide nearly every chip behind a count.
 */
const collapses = computed(() => isFinePointer.value && !isNarrow.value);

const filterKeys = computed(() => Object.keys(props.filters ?? {}));
const statementsOf = (filterKey) => props.filters?.[filterKey]?.filterStatements ?? [];

/*
 * Everything, until the widths are in — which is what puts them all on screen to be measured. A
 * scrolling rail counts nothing either, since nothing on it is ever out of reach.
 */
const visibleKeys = computed(() => {
  if (!collapses.value || groupWidths.value.size === 0 || railWidth.value === 0) {
    return filterKeys.value;
  }
  return visibleFilterKeys(filterKeys.value, groupWidths.value, railWidth.value, CHIP_GAP);
});

const hiddenKeys = computed(() => filterKeys.value.filter((key) => !visibleKeys.value.includes(key)));

const openFilterDialog = createFilterDialogOpener(() => ({
  filters: props.filters,
  filterCriteria: props.filterCriteria,
  filterGroups: props.filterGroups,
  onApply: (filters) => emit('update:filters', filters),
}));

function openDialog(openAt) {
  openFilterDialog(openAt);
}

/*
 * A chip stands for one statement, so its cross takes that statement alone. A match-all filter
 * renders one chip per value, and removing all of them from one cross would drop values the reader
 * never pointed at.
 */
function onRemove(filterKey, statementIndex) {
  emit('update:filters', removeFilterStatement(filterKey, statementIndex, props.filters));
}

/*
 * Cleared first so every chip renders for one frame, which is the only moment a hidden one has a
 * width to read.
 *
 * The rail is read in the same pass, and that is the point: the two numbers are compared against
 * each other, so taking them from different moments compares a chip set against a row it was never
 * measured in. A chip is capped at the room it has — `max-width: min(350px, 100%)` — so widths read
 * in a narrow rail are truncated ones, and against a wider rail they add up to less than it holds.
 * Everything then "fits", and the row overflows with no chip to say so.
 */
async function measure() {
  groupWidths.value = new Map();
  await nextTick();

  const rail = railRef.value;
  if (!rail) return;

  // The rail fills the row whatever is on it, so showing every chip has not changed its width.
  railWidth.value = rail.getBoundingClientRect().width;

  const widths = new Map();
  rail.querySelectorAll('[data-filter-key]').forEach((element) => {
    widths.set(element.dataset.filterKey, element.getBoundingClientRect().width);
  });
  groupWidths.value = widths;
}

/*
 * Re-measures rather than re-running the arithmetic against what was measured before, since the
 * room a chip had is part of how wide it was. Guarded on the width actually changing: hiding a chip
 * leaves the rail the size it was, so this settles rather than feeding itself.
 */
function onRailResize() {
  if (railRef.value?.getBoundingClientRect().width === railWidth.value) return;
  measure();
}

function readEnvironment() {
  isFinePointer.value = pointerQuery?.matches ?? false;
  isNarrow.value = narrowQuery?.matches ?? false;
}

let resizeObserver = null;

onMounted(async () => {
  readEnvironment();
  pointerQuery?.addEventListener?.('change', readEnvironment);
  narrowQuery?.addEventListener?.('change', readEnvironment);
  await measure();

  if (typeof ResizeObserver !== 'undefined' && railRef.value) {
    resizeObserver = new ResizeObserver(onRailResize);
    resizeObserver.observe(railRef.value);
  }
});

onBeforeUnmount(() => {
  pointerQuery?.removeEventListener?.('change', readEnvironment);
  narrowQuery?.removeEventListener?.('change', readEnvironment);
  resizeObserver?.disconnect();
});

watch(filterKeys, measure);
</script>

<style lang="scss" scoped>
/*
 * Takes the width of whatever it is placed in. Dropped into a flex row it would otherwise size to
 * its own content, and the count of how many chips fit would be taken inside a box exactly as wide
 * as the chips already showing — a measurement that can only ever shrink.
 */
.records-filter-bar {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/*
 * Fills the row whatever is in it. That is what makes the width the arithmetic runs against stable:
 * hiding a chip changes the rail's contents and not its size, so the next answer agrees with the
 * last.
 */
.records-filter-bar__rail {
  display: flex;
  flex: 1 1 0;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.records-filter-bar__group {
  flex: none;
  flex-wrap: nowrap;
}

// Collapses the max-width the call sites had grown, so no label truncates more than it did before.
.records-filter-bar__chip {
  max-width: min(350px, 100%);
  font-weight: normal;
}

/*
 * The rail scrolls instead of collapsing on a touch pointer, and at a phone width whatever the
 * pointer. It runs to both page edges with the padding moved inside, so chips pass under the edge
 * rather than stopping short of it — a scroller that stops short reads as a row that happens to be
 * clipped rather than one that continues.
 */
@media (pointer: coarse), (max-width: 599px) {
  .records-filter-bar__rail {
    overflow-x: auto;
    scrollbar-width: none;
    mask-image: linear-gradient(
      to right,
      #000 calc(100% - var(--app-comp-filter-chips-fade-width)),
      transparent
    );
  }

  .records-filter-bar__rail::-webkit-scrollbar {
    display: none;
  }

  .records-filter-bar {
    margin-inline: calc(-1 * var(--hr-page-padding, var(--app-comp-page-padding-sm)));
    padding-inline: var(--hr-page-padding, var(--app-comp-page-padding-sm));
  }
}

.records-filter-bar__overflow-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: var(--app-comp-filter-chips-overflow-panel-width);
  padding: 8px;
}
</style>
